import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { ParagraphSM, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useGuildMember } from '@/services/GuildUserService/GuildUserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { PayItForwardCheckoutGuildCta } from '../PayItForwardCheckoutGuildCta'

interface PayItForwardCheckoutCtaProps {
  showWhiteNav?: boolean
  wrapperClassName?: string
  backgroundImage?: string
  phoneImage?: string
  ctaLogo?: string
  guildDeepLink: string
  ctaButtonText: string
  ctaTitleText: string
  ctaDescriptionText?: string
}

export const PayItForwardCheckoutCta: React.FC<PayItForwardCheckoutCtaProps> = ({
  showWhiteNav = true,
  wrapperClassName,
  backgroundImage,
  phoneImage,
  ctaLogo,
  guildDeepLink,
  ctaButtonText,
  ctaTitleText,
  ctaDescriptionText,
}) => {
  const track = useSafeTrack()
  const { isGuildMember } = useGuildMember()

  const handleCtaClick = () => {
    track('Guild Checkout CTA Clicked', {
      context: 'PIF checkout web',
    })
  }
  const showDeepLink = Boolean(isGuildMember)

  return (
    <>
      <PayItForwardCheckoutGuildCta
        showTopCta={true}
        guildDeepLink={guildDeepLink}
        ctaButtonText={ctaButtonText}
        ctaTitleText={ctaTitleText}
        ctaDescriptionText={ctaDescriptionText}
        showDeepLink={showDeepLink}
        handleCtaClick={handleCtaClick}
        backgroundImage={backgroundImage}
        wrapperClassName={wrapperClassName}
        phoneImage={phoneImage}
        ctaLogo={ctaLogo}
      />
      {/* The designs for the CTA have a white nav bar only when checkout is completed, not regular checkout, so the line below adds a white background to the nav bar */}
      {showWhiteNav && <div className="z-49 absolute top-0 h-0 bg-white backdrop-blur-none lg:h-[64px] lg:w-screen" />}
      <div
        className={classNames('hidden md:h-[85vh] md:w-full flex-col p-0 md:flex', wrapperClassName)}
        style={{
          backgroundImage: backgroundImage,
        }}
      >
        <div className="relative h-full w-full items-center justify-center backdrop-blur-xl">
          <ResponsiveBackgroundImage
            src={backgroundImage ?? getCloudinaryImageUrl({ path: '/v1694104235/Show.png' })}
            className="h-full w-full bg-cover bg-center bg-no-repeat"
          />
          <div className="flex h-full w-full items-center justify-center bg-white/[0.05] backdrop-blur-xl">
            {phoneImage && (
              <img
                src={phoneImage}
                className={
                  'absolute inset-0 mx-auto mt-10 h-[70%] md:left-[12%] md:m-auto md:mx-0 lg:left-[3%] lg:h-[67%] xl:left-[16%] xl:h-[72%] 2xl:left-[24%] 2xl:h-[80%]'
                }
                alt={'phone-image'}
              />
            )}
            <div className="absolute inset-y-0 my-auto mb-10 flex h-fit w-fit max-w-[80vw] flex-col items-center rounded-2xl bg-core-gray-800/[0.92] px-4 py-8 text-center text-white backdrop-blur-3xl md:right-[10%] md:mb-auto md:max-w-[420px] md:px-6 lg:right-[8%] xl:right-[10%] 2xl:right-[15%] 3xl:right-[20%]">
              {ctaLogo && <img src={ctaLogo} className={'pb-8'} alt={'phone-image'} />}
              <TitleMD
                className="max-w-[85%] pb-4 text-lg leading-[22px] md:max-w-[75%] md:text-2xl md:leading-8"
                weight="bold"
              >
                {ctaTitleText}
              </TitleMD>
              {ctaDescriptionText && (
                <ParagraphSM className="text-sm font-light md:max-w-[90%] md:text-base md:leading-7">
                  {ctaDescriptionText}
                </ParagraphSM>
              )}
              <LinkButton
                aria-label="join-the-guild"
                href={showDeepLink ? guildDeepLink : paths.guild.index}
                variant="oxide-bright"
                className="mt-4 flex w-fit min-w-[216px] justify-center p-4 capitalize"
                onClick={handleCtaClick}
              >
                {ctaButtonText}
              </LinkButton>
            </div>
          </div>
        </div>
      </div>
      <PayItForwardCheckoutGuildCta
        showTopCta={false}
        guildDeepLink={guildDeepLink}
        ctaButtonText={ctaButtonText}
        ctaTitleText={ctaTitleText}
        ctaDescriptionText={ctaDescriptionText}
        showDeepLink={showDeepLink}
        handleCtaClick={handleCtaClick}
        backgroundImage={backgroundImage}
        wrapperClassName={wrapperClassName}
        phoneImage={phoneImage}
        ctaLogo={ctaLogo}
      />
    </>
  )
}
